import { z } from 'zod'

/***************** 표지 데이터 항목 종류 *****************/
export const COVER_DATA_TYPE = [
  'TEXT',
  'IMAGE',
  'EDITABLE_IMAGE',
  'RECT',
  'LINE',
  'TO_LOCALE_STRING',
] as const

/***************** 표지 글상자 기준점 *****************/
// middle-left·middle-center 는 편집기가 모르는 값인데 옛 템플릿(182 등)으로 만든 표지에 저장돼 있다.
// 편집기는 이 값을 기본 배치(y 이동 없음·preserveAspectRatio 기본값)로 그린다.
export const COVER_TEXT_POSITION = [
  'center',
  'bottom-right',
  'bottom-left',
  'bottom-center',
  'top-left',
  'top-right',
  'top-center',
  'center-left',
  'center-right',
  'middle-left',
  'middle-center',
] as const

/***************** 표지 이미지 영역 기준점 *****************/
export const COVER_IMAGE_POSITION = [
  'top-left',
  'top-center',
  'top-right',
  'middle-left',
  'middle-center',
  'middle-right',
  'bottom-left',
  'bottom-center',
  'bottom-right',
] as const

/***************** 디자인 템플릿 색 *****************/
export const DESIGN_TEMPLATE_COLOR = [
  'SKY',
  'BLUE_GREEN',
  'GREEN',
  'PINK',
  'ORANGE',
  'YELLOW',
  'WHITE',
  'BW',
] as const

/***************** 디자인 템플릿 언어 *****************/
export const DESIGN_TEMPLATE_LANGUAGE_TYPE = ['KOREAN', 'ENGLISH'] as const

export const CoverDataTypeSchema = z.enum(COVER_DATA_TYPE)
export type CoverDataType = z.infer<typeof CoverDataTypeSchema>

export const DesignTemplateColorSchema = z.enum(DESIGN_TEMPLATE_COLOR)
export type DesignTemplateColor = z.infer<typeof DesignTemplateColorSchema>

export const DesignTemplateLanguageTypeSchema = z.enum(DESIGN_TEMPLATE_LANGUAGE_TYPE)
export type DesignTemplateLanguageType = z.infer<typeof DesignTemplateLanguageTypeSchema>

/***************** 표지 데이터 항목 *****************/
// 표지 데이터는 서버가 해석하지 않고 저장·반환하는 JSON 이고(CoverData.kt 의 ObjectNode), 앱은 편집한 결과를
// 그대로 다시 저장한다. 템플릿 작성자가 SVG 속성을 자유롭게 넣으므로 앱이 읽는 필드만 선언하고 나머지 키는
// looseObject 로 보존한다. 선언한 필드의 모양은 이 데이터를 그리는 visual-editor 의 props 와 같아야 하고,
// 그 대응은 데이터를 에디터에 넘기는 앱이 컴파일로 확인한다.
const _SvgLengthSchema = z.union([z.number(), z.string()])

const _SvgAttributesSchema = z.looseObject({
  className: z.string().optional(),
})

const _TextRenderPropsSchema = z.looseObject({
  fontSize: z.number(),
  fontFamily: z.string(),
  maxFontSize: z.number().optional(),
  writingMode: z.string().optional(),
  x: _SvgLengthSchema.optional(),
  y: _SvgLengthSchema.optional(),
  transform: z.string().optional(),
  maxWidth: z.number().optional(),
  height: z.number().optional(),
  fontColor: z.string().optional(),
  rx: _SvgLengthSchema.optional(),
  fill: z.string().optional(),
  px: z.number().optional(),
  py: z.number().optional(),
  position: z.enum(COVER_TEXT_POSITION).optional(),
})

const _TextPropsSchema = z.looseObject({
  renderProps: _TextRenderPropsSchema,
  readOnly: z.boolean().optional(),
  disabled: z.boolean().optional(),
  defaultValue: z.string().optional(),
  hidden: z.boolean().optional(),
  opts: z
    .looseObject({
      foreignObjectProps: _SvgAttributesSchema.optional(),
      svgProps: _SvgAttributesSchema.optional(),
      textProps: _SvgAttributesSchema.optional(),
      textContainerRectProps: _SvgAttributesSchema.optional(),
    })
    .optional(),
})

const _ToLocaleStringPropsSchema = _TextPropsSchema.extend({
  defaultValue: z.number().nullable(),
  prefixValue: z.string().optional(),
  suffixValue: z.string().optional(),
})

const _BoxPropsSchema = z.looseObject({
  x: _SvgLengthSchema.optional(),
  y: _SvgLengthSchema.optional(),
  width: _SvgLengthSchema.optional(),
  height: _SvgLengthSchema.optional(),
  transform: z.string().optional(),
  className: z.string().optional(),
})

const _EditableImagePropsSchema = _BoxPropsSchema.extend({
  readOnly: z.boolean().optional(),
  hidden: z.boolean().optional(),
  position: z.enum(COVER_IMAGE_POSITION).optional(),
  imgProps: z.looseObject({ src: z.string().optional() }).optional(),
})

const _ImagePropsSchema = _BoxPropsSchema.extend({
  href: z.string().optional(),
  preserveAspectRatio: z.string().optional(),
})

const _RectPropsSchema = _BoxPropsSchema.extend({
  fill: z.string().optional(),
  rx: _SvgLengthSchema.optional(),
})

const _LinePropsSchema = z.looseObject({
  x1: _SvgLengthSchema.optional(),
  y1: _SvgLengthSchema.optional(),
  x2: _SvgLengthSchema.optional(),
  y2: _SvgLengthSchema.optional(),
  stroke: z.string().optional(),
  strokeWidth: _SvgLengthSchema.optional(),
  transform: z.string().optional(),
})

const _coverDataItem = <T extends CoverDataType, P extends z.ZodType>(type: T, props: P) =>
  z.looseObject({
    id: z.string(),
    type: z.literal(type),
    order: z.number(),
    props,
  })

export const TextCoverDataItemSchema = _coverDataItem('TEXT', _TextPropsSchema)
export type TextCoverDataItem = z.infer<typeof TextCoverDataItemSchema>

export const ImageCoverDataItemSchema = _coverDataItem('IMAGE', _ImagePropsSchema)
export type ImageCoverDataItem = z.infer<typeof ImageCoverDataItemSchema>

export const EditableImageCoverDataItemSchema = _coverDataItem(
  'EDITABLE_IMAGE',
  _EditableImagePropsSchema,
)
export type EditableImageCoverDataItem = z.infer<typeof EditableImageCoverDataItemSchema>

export const RectCoverDataItemSchema = _coverDataItem('RECT', _RectPropsSchema)
export type RectCoverDataItem = z.infer<typeof RectCoverDataItemSchema>

export const LineCoverDataItemSchema = _coverDataItem('LINE', _LinePropsSchema)
export type LineCoverDataItem = z.infer<typeof LineCoverDataItemSchema>

export const ToLocaleStringCoverDataItemSchema = _coverDataItem(
  'TO_LOCALE_STRING',
  _ToLocaleStringPropsSchema,
)
export type ToLocaleStringCoverDataItem = z.infer<typeof ToLocaleStringCoverDataItemSchema>

export const CoverDataItemSchema = z.discriminatedUnion('type', [
  TextCoverDataItemSchema,
  ImageCoverDataItemSchema,
  EditableImageCoverDataItemSchema,
  RectCoverDataItemSchema,
  LineCoverDataItemSchema,
  ToLocaleStringCoverDataItemSchema,
])
export type CoverDataItem = z.infer<typeof CoverDataItemSchema>

/***************** 표지 데이터 *****************/
// 서버 CoverData.kt 의 고정 칸이다. 칸마다 담기는 항목 종류는 서버가 정하지 않고(ObjectNode) 표지 편집기가
// 정한다. 서버 선언이 nullable 인 칸은 null 을 받는다 — 키는 빠지지 않고 null 로 온다.
// salesPrice 는 맞춤 인쇄물 템플릿을 쓸 때 앱이 키째 빼므로 optional 이기도 하다.
export const CoverDataSchema = z.object({
  headtitle: TextCoverDataItemSchema,
  title: TextCoverDataItemSchema,
  subtitle: TextCoverDataItemSchema.nullable(),
  academyName: TextCoverDataItemSchema.nullable(),
  studentName: TextCoverDataItemSchema,
  sideTitle: TextCoverDataItemSchema.nullable(),
  backTopText: TextCoverDataItemSchema.nullable(),
  backBottomText: TextCoverDataItemSchema.nullable(),
  frontLogoImage: EditableImageCoverDataItemSchema.nullable(),
  backLogoImage: EditableImageCoverDataItemSchema.nullable(),
  salesPrice: ToLocaleStringCoverDataItemSchema.nullish(),
  customData: z.array(CoverDataItemSchema),
  // 표지 전체 크기(px). 서버가 문자열로 저장한다
  width: z.string().nullable(),
  height: z.string().nullable(),
})
export type CoverData = z.infer<typeof CoverDataSchema>
export type CoverDataSlot = Exclude<keyof CoverData, 'customData' | 'width' | 'height'>

/***************** 디자인 템플릿 *****************/
// type 은 템플릿 식별 코드(DB 문자열)이고 categories 는 DB 의 자유 문자열 집합이라 좁히지 않는다.
export const DesignTemplateSummarySchema = z.object({
  id: z.number(),
  type: z.string(),
  languageType: DesignTemplateLanguageTypeSchema,
  categories: z.array(z.string()),
  color: DesignTemplateColorSchema,
  thumbnailImageUrl: z.string(),
})
export type DesignTemplateSummary = z.infer<typeof DesignTemplateSummarySchema>

export const DesignTemplateSchema = DesignTemplateSummarySchema.extend({
  coverData: CoverDataSchema,
})
export type DesignTemplate = z.infer<typeof DesignTemplateSchema>

/***************** 표지 이미지 업로드 *****************/
// 표지에 넣을 로고 이미지를 올릴 주소. 앱이 presigned 주소로 파일을 올린 뒤 imageUrl 을 표지 데이터에 싣는다
export const ImageUploadUrlSchema = z.object({
  imagePresignedUrl: z.string(),
  imageUrl: z.string(),
})
export type ImageUploadUrl = z.infer<typeof ImageUploadUrlSchema>
